import { createFileRoute, Link, useParams, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useStore, useIsLoaded } from '../../../../store';
import { Badge, Modal, CopyButton } from '../../../../components/shared';
import { CardSkeleton } from '../../../../components/skeletons';
import { EditProfileDialog } from '../../../../components/profiles/EditProfileDialog';
import { ProfileMappingsManager } from '../../../../components/profiles/ProfileMappingsManager';
import { useProfiles, usePlatformAdmin } from '../../../../hooks';
import { profileService } from '../../../../services/profileService';
import { GenerateWorkerTokenDialog } from '../../../../components/profiles/GenerateWorkerTokenDialog';
import type { ProfileFormData } from '../../../../types/profile.types';
import styles from '../../../../components/ProjectsView.module.css';

export const Route = createFileRoute('/projects/$projectId/profiles/$profileId')({
  component: ProfileDetailRoute,
});

function ProfileDetailRoute() {
  const { projectId, profileId } = useParams({ from: '/projects/$projectId/profiles/$profileId' });
  const navigate = useNavigate();
  const isConnected = useStore(s => s.connectionState === 'connected');
  const projects = useStore(s => s.projects);
  const profiles = useStore(s => s.profiles);
  const applications = useStore(s => s.applications);
  const appPoolCoverage = useStore(s => s.appPoolCoverage);

  const project = projects[projectId];

  const { toggleProfileStatus, updateProfile, deleteProfile, forcePushConfig, generateWorkerToken } = useProfiles(projectId);
  const { isPlatformAdmin } = usePlatformAdmin();

  useEffect(() => {
    if (!isPlatformAdmin) {
      navigate({ to: '/projects/$projectId/applications', params: { projectId } });
    }
  }, [isPlatformAdmin, navigate, projectId]);

  if (!isPlatformAdmin) return null;

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isMappingsOpen, setIsMappingsOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPushing, setIsPushing] = useState(false);
  const [isTokenDialogOpen, setIsTokenDialogOpen] = useState(false);

  const profile = profiles[profileId];
  const mappedApps = Object.entries(appPoolCoverage)
    .filter(([, entries]) => entries.some(e => e.poolId === profileId))
    .map(([appId]) => applications[appId])
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleToggleStatus = async () => {
    if (profile) {
      await toggleProfileStatus(profile.id, !profile.active);
    }
  };

  const handleUpdate = async (profileId: string, data: Partial<ProfileFormData>) => {
    const result = await updateProfile(profileId, data);
    if (result.success) {
      setIsEditOpen(false);
    }
    return result;
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);

    const result = await deleteProfile(profileId);

    if (result.success) {
      navigate({ to: '/projects/$projectId/profiles', params: { projectId } });
    } else {
      setDeleteError(result.error || 'Failed to delete profile');
      setIsDeleting(false);
    }
  };

  const handleForcePushConfig = async () => {
    setIsPushing(true);
    await forcePushConfig(profileId);
    setIsPushing(false);
  };

  const profilesLoaded = useIsLoaded('profiles');

  // Show skeleton while profiles are loading
  if (!profilesLoaded) {
    return (
      <div className={styles.container}>
        <div className={styles.breadcrumb}>
          <Link to="/projects" className={styles.breadcrumbLink}>
            Projects
          </Link>
          <span className={styles.breadcrumbSeparator}>/</span>
          <Link to="/projects/$projectId/profiles" params={{ projectId }} className={styles.breadcrumbLink}>
            {project?.name || 'Project'}
          </Link>
          <span className={styles.breadcrumbSeparator}>/</span>
          <span>Loading...</span>
        </div>
        <CardSkeleton />
      </div>
    );
  }

  // Show error only after data is loaded and profile truly doesn't exist
  if (!profile) {
    return (
      <div className={styles.container}>
        <div className={styles.errorMessage}>
          <span className={styles.errorText}>Deployment profile not found</span>
          <Link to="/projects/$projectId/profiles" params={{ projectId }} className={styles.errorClose}>
            ← Back to Profiles
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumb */}
      <div className={styles.breadcrumb}>
        <Link to="/projects" className={styles.breadcrumbLink}>
          Projects
        </Link>
        <span className={styles.breadcrumbSeparator}>/</span>
        <Link to="/projects/$projectId/profiles" params={{ projectId }} className={styles.breadcrumbLink}>
          {project?.name || 'Project'}
        </Link>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span>{profile.name}</span>
      </div>

      <div className={styles.detailHeader}>
        <div className={styles.detailHeaderContent}>
          <h1 className={styles.detailTitle}>
            {profile.name}
            <Badge variant={profile.active ? 'success' : 'default'}>
              {profile.active ? 'Active' : 'Inactive'}
            </Badge>
          </h1>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button
            onClick={handleToggleStatus}
            disabled={!isConnected}
            className={styles.button}
          >
            {profile.active ? 'Deactivate' : 'Activate'}
          </button>
          <button
            onClick={() => setIsEditOpen(true)}
            disabled={!isConnected}
            className={`${styles.button} ${styles.buttonSecondary}`}
          >
            Edit
          </button>
          <button
            onClick={handleForcePushConfig}
            disabled={!isConnected || isPushing}
            className={`${styles.buttonSmall} ${styles.buttonSecondary}`}
            title="Force push config to pool"
          >
            {isPushing ? 'Pushing...' : 'Push Config'}
          </button>
          {isPlatformAdmin && (
            <button
              onClick={() => setIsTokenDialogOpen(true)}
              disabled={!isConnected}
              className={`${styles.buttonSmall} ${styles.buttonSecondary}`}
              title="Generate worker admission token"
            >
              Worker Token
            </button>
          )}
          <button
            onClick={() => setIsDeleteOpen(true)}
            disabled={!isConnected}
            className={`${styles.buttonSmall} ${styles.buttonDelete}`}
            title="Delete profile"
          >
            Delete
          </button>
        </div>
      </div>

      <div className={styles.metadataStrip}>
        <div className={styles.metadataItem}>
          <div className={styles.metadataLabel}>Profile ID</div>
          <div className={styles.metadataValue}>
            {profile.id}
            <CopyButton value={profile.id} aria-label="Copy Profile ID" />
          </div>
        </div>
        <div className={styles.metadataItem}>
          <div className={styles.metadataLabel}>Type</div>
          <div className={styles.metadataValue}>{profileService.formatProfileType(profile.type)}</div>
        </div>
        <div className={styles.metadataItem}>
          <div className={styles.metadataLabel}>Region</div>
          <div className={styles.metadataValue}>{profile.region}</div>
        </div>
        <div className={styles.metadataItem}>
          <div className={styles.metadataLabel}>Applications</div>
          <div className={styles.metadataValue}>{mappedApps.length}</div>
        </div>
        <div className={styles.metadataItem}>
          <div className={styles.metadataLabel}>Last Updated</div>
          <div className={styles.metadataValue}>
            {profile.updatedAt ? new Date(profile.updatedAt).toLocaleDateString() : 'N/A'}
          </div>
        </div>
      </div>

      {profile.notes && (
        <div style={{
          padding: 'var(--space-3)',
          background: 'var(--bg-secondary)',
          border: 'var(--border-width) solid var(--border)',
          marginBottom: 'var(--space-4)',
          fontStyle: 'italic',
          fontSize: 'var(--text-sm)',
          color: 'var(--text-secondary)'
        }}>
          {profile.notes}
        </div>
      )}

      <div className={styles.detailSection}>
        <div className={styles.detailSectionHeader}>
          <h2 className={styles.detailSectionTitle}>Mapped Applications</h2>
          <button
            onClick={() => setIsMappingsOpen(true)}
            disabled={!isConnected}
            className={`${styles.buttonSmall} ${styles.buttonSecondary}`}
          >
            Edit Mappings
          </button>
        </div>

        {mappedApps.length === 0 ? (
          <div className={styles.emptyState}>
            No applications mapped to this profile yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {mappedApps.map(app => (
              <Link
                key={app.id}
                to="/projects/$projectId/applications/$appId"
                params={{ projectId, appId: app.id }}
                className={styles.appLinkCard}
              >
                <span style={{
                  fontWeight: 'var(--weight-medium)',
                  color: 'var(--text-primary)',
                  fontSize: 'var(--text-sm)'
                }}>
                  {app.name}
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>

      {isEditOpen && (
        <EditProfileDialog
          isOpen={true}
          profile={profile}
          onClose={() => setIsEditOpen(false)}
          onUpdate={handleUpdate}
        />
      )}

      {isMappingsOpen && (
        <ProfileMappingsManager
          isOpen={true}
          profileId={profile.id}
          profileName={profile.name}
          projectId={projectId}
          onClose={() => setIsMappingsOpen(false)}
        />
      )}

      {isTokenDialogOpen && (
        <GenerateWorkerTokenDialog
          profileId={profileId}
          profileName={profile.name}
          onClose={() => setIsTokenDialogOpen(false)}
          generateWorkerToken={generateWorkerToken}
        />
      )}

      {isDeleteOpen && (
        <Modal
          isOpen={true}
          title="Delete Deployment Profile"
          subtitle={<span>Are you sure you want to delete <strong>{profile.name}</strong>?</span>}
          onClose={() => !isDeleting && setIsDeleteOpen(false)}
          size="small"
        >
          <div style={{ padding: 'var(--space-4)' }}>
            <p style={{ marginBottom: 'var(--space-4)', color: 'var(--text-secondary)' }}>
              This action cannot be undone. All application mappings associated with this profile will also be removed.
            </p>

            {deleteError && (
              <div className={styles.errorMessage} style={{ marginBottom: 'var(--space-4)' }}>
                <span className={styles.errorText}>{deleteError}</span>
              </div>
            )}

            <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setIsDeleteOpen(false)}
                disabled={isDeleting}
                className={`${styles.button} ${styles.buttonSecondary}`}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className={`${styles.buttonSmall} ${styles.buttonDelete}`}
              >
                {isDeleting ? 'Deleting...' : 'Delete Profile'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
